import React, { createContext, useContext, useState, useEffect } from 'react';
import CompanyService from '../services/companyService';
import AuthService from '../services/authService';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

// Helper function to get saved company from localStorage
const getSavedCompanyFromStorage = () => {
  try {
    const savedCompanyJson = localStorage.getItem('selectedCompany');
    if (savedCompanyJson) {
      return JSON.parse(savedCompanyJson);
    }
  } catch (err) {
    console.error('Error parsing saved company from localStorage:', err);
    localStorage.removeItem('selectedCompany');
  }
  return null;
};

export const CompanyProvider = ({ children }) => {
  const currentUser = AuthService.getCurrentUser();
  
  // Initialize selectedCompany from localStorage on mount
  const [selectedCompany, setSelectedCompany] = useState(() => getSavedCompanyFromStorage());
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Fetch companies when user changes
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!currentUser?.id) {
        setCompanies([]);
        setSelectedCompany(null);
        localStorage.removeItem('selectedCompany');
        localStorage.removeItem('selectedCompanyId');
        return;
      }

      setLoadingCompanies(true);
      try {
        const result = await CompanyService.getCompaniesByUserId(currentUser.id);
        if (result.success) {
          const companyList = result.data || [];
          setCompanies(companyList);
          
          // If there's a saved company in localStorage, try to restore it
          const savedCompany = getSavedCompanyFromStorage();
          if (savedCompany && savedCompany.id && companyList.length > 0) {
            // Try to find company by ID (handle both string and number comparison)
            const foundCompany = companyList.find(c => 
              String(c.id) === String(savedCompany.id) || c.id === savedCompany.id
            );
            if (foundCompany) {
              // Update with fresh data from server
              setSelectedCompany(foundCompany);
            } else {
              // If saved company not found in list, clear it
              setSelectedCompany(null);
              localStorage.removeItem('selectedCompany');
              localStorage.removeItem('selectedCompanyId');
            }
          }
        }
      } catch (err) {
        console.error('Fetch companies error:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [currentUser?.id]);

  // Save selected company to localStorage
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      try {
        // Save entire company object as JSON for better reliability
        localStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
        // Also save ID for backward compatibility
        localStorage.setItem('selectedCompanyId', String(selectedCompany.id));
        // Dispatch custom event for other components to listen
        window.dispatchEvent(new CustomEvent('companyChanged', { detail: selectedCompany }));
      } catch (err) {
        console.error('Error saving company to localStorage:', err);
      }
    } else {
      localStorage.removeItem('selectedCompany');
      localStorage.removeItem('selectedCompanyId');
      window.dispatchEvent(new CustomEvent('companyChanged', { detail: null }));
    }
  }, [selectedCompany]);

  const value = {
    selectedCompany,
    setSelectedCompany,
    companies,
    loadingCompanies,
    companyId: selectedCompany?.id || null,
    taxId: selectedCompany?.vergiKimlikNo || null,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
